import React, { useMemo, useState, useRef } from 'react';
import {
  Drawer,
  Row,
  Col,
  Input,
} from 'antd';
import { useSelector } from 'react-redux';
import { DownOutlined, RightOutlined, LoadingOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';

import { getProjects, getPhaseIdMap, getProjectIdMap } from '../../../../../state/selectors';
import { includesTerm } from '../../../../../helpers/helpers';
import { getProjectOptions } from '../../../../../clock/ManualEntry/manualEntryHelpers';
import { EQUIPMENT_CATEGORY_ID } from '../../../../../helpers/costcodeHelpers';
import { sortByObjProperty } from '../../../../../common/helpers';
import OnTraccrCheckbox from '../../../../../common/inputs/OnTraccrCheckbox';
import Colors from '../../../../../constants/Colors';
import { FOOTER_HEIGHT } from '../../../../../constants/Styles';
import DrawerSubmitFooter from '../../../../../common/containers/DrawerSubmitFooter';
import {
  buildPhaseMapByProjectCostcode,
  expandCostcodePickerRows,
  formatCostcodePickerLabel,
  getCostcodeColumnKey,
  getCostcodePickerRowKey,
} from './buildWizardGridPayload';

export default function TimeTrackingGridWizardAddCostcodeDrawer({
  visible,
  onClose,
  selectedCostcodes,
  setSelectedCostcodes,
  divisions,
  configProps,
  costcodes,
  fetchProjectCostcodes,
  lockedProjectId,
  restrictToGlobalCostcodesOnly = false,
}) {
  const projects = useSelector(getProjects);
  const phases = useSelector((state) => state.costcodes.phases);
  const phaseMap = useSelector(getPhaseIdMap);
  const phaseProjectCostcodeMap = useMemo(
    () => buildPhaseMapByProjectCostcode(phases),
    [phases],
  );
  const projectMap = useSelector(getProjectIdMap);
  const { settings = {} } = useSelector((state) => state.settings.company || {});

  const [searchText, setSearchText] = useState('');
  const [selectedProjects, setSelectedProjects] = useState(new Set());
  const [loadingProjectIds, setLoadingProjectIds] = useState(new Set());
  const [selectedCostcodesInner, setSelectedCostcodesInner] = useState([]);
  const [prevSelectedCostcodes, setPrevSelectedCostcodes] = useState([]);
  const [prevVisible, setPrevVisible] = useState(visible);

  // Tracks which project rows have been expanded (and thus fetched) in this drawer session.
  const expandedProjectIds = useRef(new Set());

  if (visible && prevSelectedCostcodes !== selectedCostcodes) {
    setSelectedCostcodesInner(selectedCostcodes);
    setPrevSelectedCostcodes(selectedCostcodes);
  }

  if (prevVisible !== visible) {
    setPrevVisible(visible);

    if (!visible) {
      setSelectedProjects(new Set());
      setSelectedCostcodesInner([]);
      setPrevSelectedCostcodes([]);
      setSearchText('');
      setLoadingProjectIds(new Set());
      expandedProjectIds.current = new Set();
    } else if (lockedProjectId != null) {
      setSelectedProjects(new Set([String(lockedProjectId)]));
    } else if (restrictToGlobalCostcodesOnly) {
      setSelectedProjects(new Set(['Global']));
    }
  }

  const selectedCostcodeKeySet = useMemo(
    () => new Set(selectedCostcodesInner.map((selection) => getCostcodeColumnKey(selection))),
    [selectedCostcodesInner],
  );

  const filteredCostcodeMap = useMemo(() => {
    const projectCCMap = {};
    costcodes.forEach((cc) => {
      if (lockedProjectId != null && String(cc.projectId || '') !== String(lockedProjectId)) {
        return;
      }
      if (restrictToGlobalCostcodesOnly && cc.projectId) {
        return;
      }
      if (
        cc.active
        && (cc.canClockIn
          || cc.categoryIds?.includes(EQUIPMENT_CATEGORY_ID))
      ) {
        const relevantProject = cc.projectId ?? 'Global';
        expandCostcodePickerRows(cc, phaseProjectCostcodeMap).forEach((pickerRow) => {
          const label = formatCostcodePickerLabel(pickerRow, phaseMap);
          if (searchText && !includesTerm(label, searchText)) return;
          if (!projectCCMap[relevantProject]) {
            projectCCMap[relevantProject] = [];
          }
          projectCCMap[relevantProject].push({ ...pickerRow, label });
        });
      }
    });
    return projectCCMap;
  }, [costcodes, phaseProjectCostcodeMap, phaseMap, searchText, lockedProjectId, restrictToGlobalCostcodesOnly]);

  const activeProjects = useMemo(() => projects.filter((project) => project.active), [projects]);

  const projectOptions = useMemo(() => {
    const baseOptions = getProjectOptions({
      activeProjects,
      divisionIds: divisions,
      projectId: null,
      settings,
    }).sort(sortByObjProperty('label'));

    if (!configProps.isProjectRequired) {
      baseOptions.unshift({ label: 'Global', value: 'Global' });
    }

    return baseOptions
      .map((project) => {
        const { value } = project;
        const costcodesForProject = filteredCostcodeMap[value] ?? [];
        const selectedCount = selectedCostcodesInner.filter(
          (selection) => (selection.project?.id ?? 'Global') === value,
        ).length;
        return {
          ...project,
          selectedCostcodeCount: selectedCount,
          costcodeCount: costcodesForProject.length,
        };
      })
      .filter((project) => {
        if (lockedProjectId == null && !restrictToGlobalCostcodesOnly) return true;
        return project.costcodeCount > 0;
      });
  }, [
    activeProjects,
    divisions,
    settings,
    configProps,
    filteredCostcodeMap,
    selectedCostcodesInner,
    lockedProjectId,
    restrictToGlobalCostcodesOnly,
  ]);

  const onProjectSelect = async (value) => {
    const newSelectedProjects = new Set(selectedProjects);
    if (selectedProjects.has(value)) {
      newSelectedProjects.delete(value);
    } else {
      newSelectedProjects.add(value);
      if (value !== 'Global' && fetchProjectCostcodes && !expandedProjectIds.current.has(value)) {
        expandedProjectIds.current.add(value);
        setLoadingProjectIds((prev) => new Set([...prev, value]));
        await fetchProjectCostcodes(value);
        setLoadingProjectIds((prev) => {
          const next = new Set(prev);
          next.delete(value);
          return next;
        });
      }
    }
    setSelectedProjects(newSelectedProjects);
  };

  const onCostcodeSelect = (cc, projectKey) => {
    const project = projectKey && projectKey !== 'Global'
      ? (() => {
        const p = projectMap[projectKey];
        return p ? { id: p.id, name: p.name, number: p.number } : null;
      })()
      : null;

    const phaseEntity = cc.phaseId ? phaseMap[cc.phaseId] : null;
    const phase = phaseEntity
      ? { id: phaseEntity.id, name: phaseEntity.name }
      : null;

    const selection = {
      costcodeId: cc.id,
      project,
      phase,
      costcode: {
        id: cc.id,
        name: cc.name,
        code: cc.code,
        fullText: cc.code ? `${cc.code} - ${cc.name}` : cc.name,
      },
    };
    const selectionKey = getCostcodeColumnKey(selection);

    const isSelected = selectedCostcodesInner.some(
      (existing) => getCostcodeColumnKey(existing) === selectionKey,
    );
    if (isSelected) {
      setSelectedCostcodesInner(
        selectedCostcodesInner.filter(
          (existing) => getCostcodeColumnKey(existing) !== selectionKey,
        ),
      );
      return;
    }

    setSelectedCostcodesInner([...selectedCostcodesInner, selection]);
  };

  const isPickerRowSelected = (cc, projectKey) => {
    const project = projectKey && projectKey !== 'Global'
      ? { id: projectMap[projectKey]?.id } : null;
    const phase = cc.phaseId ? { id: cc.phaseId } : null;
    return selectedCostcodeKeySet.has(getCostcodeColumnKey({
      costcodeId: cc.id,
      project,
      phase,
      costcode: { id: cc.id },
    }));
  };

  const onSearch = (e) => {
    setSearchText(e.target.value);
  };

  const onSubmit = () => {
    setSelectedCostcodes(selectedCostcodesInner);
    onClose();
  };

  return (
    <Drawer
      visible={visible}
      onClose={onClose}
      title="Add Cost Codes"
      width={600}
      bodyStyle={{
        padding: 0,
        marginBottom: FOOTER_HEIGHT,
        overflowY: 'auto',
      }}
      headerStyle={{ border: 'none' }}
    >
      <div className="time-tracking-grid-wizard-add-costcode-drawer-description">
        Tap cost codes to add them as columns in
        your grid
      </div>
      <div className="time-tracking-grid-wizard-add-costcode-drawer-search-container">
        <Input.Search
          placeholder="Search cost codes..."
          onChange={onSearch}
          className="time-tracking-grid-wizard-add-costcode-drawer-search"
        />
      </div>
      {projectOptions.map(({
        label, value, selectedCostcodeCount, costcodeCount,
      }) => (
        <>
          <Row
            key={value}
            onClick={() => onProjectSelect(value)}
            className="time-tracking-grid-wizard-add-costcode-drawer-project-row"
          >
            <Col span={20}>
              <div className="time-tracking-grid-wizard-add-costcode-drawer-project-row-label-container">
                <div className="time-tracking-grid-wizard-add-costcode-drawer-project-row-label">
                  {label}
                </div>
                {selectedCostcodeCount > 0 && (
                  <div className="time-tracking-grid-wizard-add-costcode-drawer-project-row-selected-costcode-count">
                    {selectedCostcodeCount} selected
                  </div>
                )}
              </div>
              <div className="time-tracking-grid-wizard-add-costcode-drawer-project-row-costcode-count">
                {loadingProjectIds.has(value) && <LoadingOutlined />}
                {!loadingProjectIds.has(value)
                  && (value === 'Global' || expandedProjectIds.current.has(value))
                  && `${costcodeCount} cost codes`}
              </div>
            </Col>
            <Col span={4} className="time-tracking-grid-wizard-add-costcode-drawer-project-row-icon-container">
              {selectedProjects.has(value)
                ? <DownOutlined className="time-tracking-grid-wizard-add-costcode-drawer-project-row-icon" />
                : <RightOutlined className="time-tracking-grid-wizard-add-costcode-drawer-project-row-icon" />}
            </Col>
          </Row>
          {!!selectedProjects.has(value) && filteredCostcodeMap[value]?.map((cc) => (
            <OnTraccrCheckbox
              key={getCostcodePickerRowKey(cc, value)}
              label={cc.label}
              value={isPickerRowSelected(cc, value)}
              onChange={() => onCostcodeSelect(cc, value)}
              textStyle={{
                fontFamily: 'open-sans-500',
                fontSize: 14,
                color: Colors.GRAY_950,
              }}
              checkboxClasses={[
                'time-tracking-grid-wizard-add-costcode-drawer-costcode-checkbox',
                isPickerRowSelected(cc, value) ? 'selected' : '',
              ]}
            />
          ))}
        </>
      ))}
      <DrawerSubmitFooter
        onClose={onClose}
        submitTitle="Done"
        onSubmit={onSubmit}
      />
    </Drawer>
  );
}

export const wizardCostcodeSelectionPropType = PropTypes.shape({
  costcodeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  project: PropTypes.oneOfType([
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    }),
    PropTypes.oneOf([null]),
  ]),
  phase: PropTypes.oneOfType([
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    }),
    PropTypes.oneOf([null]),
  ]),
  costcode: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    code: PropTypes.string,
    fullText: PropTypes.string.isRequired,
  }).isRequired,
});

TimeTrackingGridWizardAddCostcodeDrawer.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedCostcodes: PropTypes.arrayOf(wizardCostcodeSelectionPropType).isRequired,
  setSelectedCostcodes: PropTypes.func.isRequired,
  divisions: PropTypes.arrayOf(PropTypes.string).isRequired,
  configProps: PropTypes.shape({
    isProjectRequired: PropTypes.bool,
  }).isRequired,
  costcodes: PropTypes.arrayOf(PropTypes.shape({})),
  fetchProjectCostcodes: PropTypes.func,
  lockedProjectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  restrictToGlobalCostcodesOnly: PropTypes.bool,
};

TimeTrackingGridWizardAddCostcodeDrawer.defaultProps = {
  costcodes: [],
  fetchProjectCostcodes: null,
  lockedProjectId: undefined,
  restrictToGlobalCostcodesOnly: false,
};
